import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import api from '../api';
import { getProcurementAccess, ProcurementAccess, ProcurementFeature } from '../api/procurement';
import { searchAlertUnread } from '../api/searchAlerts';
import { useAuth } from './AuthContext';

interface AccessState {
  access: ProcurementAccess | null;
  loading: boolean;
  error: boolean;
  allowed: (feature: ProcurementFeature) => boolean;
  refresh: () => Promise<void>;
  unreadCount: number | null;
  refreshUnread: () => Promise<void>;
}
const denied: AccessState = { access: null, loading: false, error: false, allowed: () => false,
  refresh: async () => {}, unreadCount: null, refreshUnread: async () => {} };
const ProcurementAccessContext = createContext<AccessState>(denied);
export const useProcurementAccess = () => useContext(ProcurementAccessContext);

export function ProcurementAccessProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading, subscription } = useAuth();
  const owner = user ? `${user.email}:${user.role}:${user.is_active}` : '';
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const request = useRef<AbortController | null>(null);
  const unreadRequest = useRef<AbortController | null>(null);
  const lastDeniedRefresh = useRef(0);
  const [snapshot, setSnapshot] = useState<{ owner: string; access: ProcurementAccess | null; loading: boolean; error: boolean }>({ owner: '', access: null, loading: true, error: false });
  const [unread, setUnread] = useState<{ owner: string; value: number | null }>({ owner: '', value: null });
  const refresh = useCallback(async () => {
    request.current?.abort();
    const controller = new AbortController(); request.current = controller;
    setSnapshot((previous) => previous.owner === owner && previous.access
      && (!previous.access.next_change_at || Date.parse(previous.access.next_change_at) > Date.now())
      ? { ...previous, error: false } : { owner, access: null, loading: !!owner, error: false });
    if (!owner) return;
    try {
      const access = await getProcurementAccess(controller.signal);
      if (!controller.signal.aborted && ownerRef.current === owner) setSnapshot({ owner, access, loading: false, error: false });
    } catch {
      if (!controller.signal.aborted && ownerRef.current === owner) setSnapshot({ owner, access: null, loading: false, error: true });
    }
  }, [owner]);
  // Recheck after purchases/cancellation without trusting local subscription labels.
  const subscriptionVersion = JSON.stringify(subscription);
  useEffect(() => {
    if (!authLoading) void refresh();
    return () => { request.current?.abort(); unreadRequest.current?.abort(); };
  }, [refresh, authLoading, subscriptionVersion]);
  const access = snapshot.owner === owner && !authLoading ? snapshot.access : null;
  const expired = !!access?.next_change_at && (!Number.isFinite(Date.parse(access.next_change_at)) || Date.parse(access.next_change_at) <= Date.now());
  const allowed = useCallback((feature: ProcurementFeature) => !!access?.features?.[feature] && !expired, [access, expired]);
  const canReadHistory = !!owner && !!access;
  useEffect(() => {
    if (!access?.next_change_at) return;
    const delay = Date.parse(access.next_change_at) - Date.now();
    if (!Number.isFinite(delay)) return;
    const timer = window.setTimeout(() => { void refresh(); }, Math.min(2147483647, Math.max(1000, delay)));
    return () => window.clearTimeout(timer);
  }, [access, refresh]);
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState !== 'hidden') void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);
  useEffect(() => {
    const interceptor = api.interceptors.response.use(undefined, (error) => {
      if (error?.response?.status === 403 && /^(?:\/lots|\/suppliers|\/search-alerts)(?:\/|$)/.test(error.config?.url || '')
          && Date.now() - lastDeniedRefresh.current > 1000) {
        // Concurrent forbidden requests need one entitlement refresh, not a
        // remount/refetch loop if an endpoint denies for another reason.
        lastDeniedRefresh.current = Date.now();
        setSnapshot({ owner, access: null, loading: true, error: false });
        void refresh();
      }
      return Promise.reject(error);
    });
    return () => api.interceptors.response.eject(interceptor);
  }, [refresh, owner]);
  const refreshUnread = useCallback(async () => {
    if (ownerRef.current !== owner) return;
    unreadRequest.current?.abort();
    if (!canReadHistory) { setUnread({ owner, value: null }); return; }
    const controller = new AbortController(); unreadRequest.current = controller;
    try {
      const result = await searchAlertUnread(controller.signal);
      if (!controller.signal.aborted && ownerRef.current === owner) setUnread({ owner, value: result.unread_count });
    } catch { if (!controller.signal.aborted && ownerRef.current === owner) setUnread({ owner, value: null }); }
  }, [owner, canReadHistory]);
  useEffect(() => {
    void refreshUnread();
    if (!canReadHistory) return;
    const timer = window.setInterval(() => { if (document.visibilityState !== 'hidden') void refreshUnread(); }, 60000);
    return () => { window.clearInterval(timer); unreadRequest.current?.abort(); };
  }, [refreshUnread, canReadHistory]);
  return <ProcurementAccessContext.Provider value={{ access, allowed, refresh,
    loading: authLoading || (!!owner && snapshot.owner !== owner) || snapshot.loading,
    error: snapshot.owner === owner && snapshot.error, unreadCount: canReadHistory && unread.owner === owner ? unread.value : null,
    refreshUnread }}>{children}</ProcurementAccessContext.Provider>;
}
